import { Loader2, OctagonX, TriangleAlert } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Typography } from '@/components/ui/typography';
import type { IntegrationStatus } from '../../services/pmsConnectionApi';
import { integrationStatusCopy as copy } from './copy';
import { formatPausedBy } from './formatAttribution';

interface Props {
  vendorName: string;
  status: Extract<IntegrationStatus, { kind: 'disabled' }>;
  canManage: boolean;
  onReenable: () => void;
  isReenabling: boolean;
  error: string | null;
}

/** Warning banner above the vendor settings tabs while the integration is paused (spec §5.1). */
export const DisabledIntegrationBanner = ({ vendorName, status, canManage, onReenable, isReenabling, error }: Props) => {
  const pausedBy = formatPausedBy(status.disabledBy, status.disabledAt);
  return (
    <div className="mx-5 mt-5 flex flex-col gap-3">
      <Alert variant="warning" layout="row" role="alert" className="flex-col rounded-xl sm:flex-row sm:items-start sm:justify-between">
        <div className="flex gap-3">
          <TriangleAlert className="h-5 w-5 shrink-0" aria-hidden="true" />
          <div className="flex flex-col gap-1">
            <AlertTitle>{copy.banner.title}</AlertTitle>
            <AlertDescription>{copy.banner.body(vendorName)}</AlertDescription>
            {pausedBy && (
              <Typography variant="small" className="text-muted-foreground">{pausedBy}</Typography>
            )}
          </div>
        </div>
        {canManage && (
          <Button type="button" onClick={onReenable} disabled={isReenabling} className="shrink-0">
            {isReenabling && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
            {isReenabling ? copy.banner.reenabling : copy.banner.reenable}
          </Button>
        )}
      </Alert>
      {error && (
        <Alert variant="destructive" role="alert" className="rounded-xl">
          <OctagonX className="h-4 w-4" aria-hidden="true" />
          <AlertTitle>{copy.banner.failedTitle}</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
    </div>
  );
};
